import React, { useEffect, useMemo, useState } from "react";

import { vi } from "date-fns/locale";
import {
  XIcon,
  GripVerticalIcon,
  CheckIcon,
  EyeIcon,
  TableIcon,
  LayoutGridIcon,
  Rows2Icon,
  Rows3Icon,
  Rows4Icon,
  ListChecksIcon,
} from "lucide-react";
import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  type DraggableLocation,
  Droppable,
  type DroppableProvided,
  type DroppableStateSnapshot,
} from "@hello-pangea/dnd";

import {
  Stack,
  Box,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Checkbox,
  ListSubheader,
  useTheme,
  alpha,
} from "@mui/material/";

import { navHover, navSelected, Button, MENU_ITEM_SX, MENU_ITEM_DENSE_SX } from "@repo/ui";
import { vhMinus } from 'utils/textZoom'
// Direct file import, not the `components` barrel — this file is part of that barrel.
import { toolbarLabelSx } from "../ActionLine/toolbarCollapse";
export interface LayoutOptionsProps {
  apiRef?: any;
  disabled?: boolean;
  density?: string;
  layout?: string;
  columns?: [];
  visibilityModel?: {};
  // Reset column order + visibility + density to their defaults.
  onResetColumns?: () => void;
}

export const LayoutOptions = (props: LayoutOptionsProps) => {
  const {
    apiRef = false,
    disabled = false,
    density = "standard",
    layout = "table",
    columns,
    visibilityModel = {},
    onResetColumns,
  } = props;

  const theme = useTheme();
  // A column is hidden only when its value is explicitly `false`. MUI keeps the key
  // in the model (as `true`) after you re-show a column, so keying off presence
  // (Object.keys) would leave default-hidden columns permanently unchecked. (VERA-455)
  let hiddenColumns = Object.keys(visibilityModel).filter(
    (field) => (visibilityModel as Record<string, boolean>)[field] === false,
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [internalColumnOrder, setInternalColumnOrder] = useState(columns);
  // console.log(internalColumnOrder, columns)
  const open = Boolean(anchorEl);

  // A re-render pulse for grid changes that this menu must reflect but that don't
  // change its props: a column-header drag and our own `setColumnIndex` (both fire
  // `columnOrderChange`). Keyed on `open` because `apiRef.current` is null at mount
  // (a ref change doesn't re-run an effect), so opening the menu guarantees the grid
  // is live by the time we subscribe.
  const [, setOrderPulse] = useState(0);
  useEffect(() => {
    const api = apiRef && apiRef.current;
    if (!api || typeof api.subscribeEvent !== "function") return;
    return api.subscribeEvent("columnOrderChange", () =>
      setOrderPulse((v) => v + 1)
    );
  }, [apiRef, open]);

  // The static `columns` prop reordered to match the grid's CURRENT field order.
  // Computed inline (NOT memoized) so it is always fresh on every render — restoreState
  // (the Reset button) doesn't fire `columnOrderChange`, but it re-renders this menu via
  // the visibility/density prop changes, and an inline read picks the new order up. The
  // subscription above covers order-only changes (header drag) that have no prop change.
  const liveColumns =
    apiRef && apiRef.current && apiRef.current.getAllColumns
      ? apiRef.current.getAllColumns()
      : null;
  // Columns hidden BY DEFAULT (visibilityModel, not a user's own toggle-off)
  // sort to the bottom of the picker, below every visible one — the drag
  // handles still work within each group, so a user reordering columns isn't
  // fighting this, it's just partitioned into "shown" then "not shown".
  const defaultHiddenFields = new Set(
    Object.keys(visibilityModel).filter(
      (field) => (visibilityModel as Record<string, boolean>)[field] === false,
    ),
  );
  const orderedColumns = liveColumns
    ? (() => {
        const pos = new Map(liveColumns.map((c, i) => [c.field, i]));
        return [...columns].sort((a, b) => {
          const aHidden = defaultHiddenFields.has(a.field) ? 1 : 0;
          const bHidden = defaultHiddenFields.has(b.field) ? 1 : 0;
          if (aHidden !== bHidden) return aHidden - bHidden;
          return (pos.get(a.field) ?? 0) - (pos.get(b.field) ?? 0);
        });
      })()
    : columns;

  // Only offer Reset when the layout is actually non-default — column order changed (drag),
  // any column hidden, or a non-default density. Parity with the filters "Clear", which only
  // shows when there's something to clear. (Compares the live field order to the default `columns`.)
  const isLayoutModified =
    (orderedColumns ?? []).map((c: any) => c.field).join("|") !==
      (columns ?? []).map((c: any) => c.field).join("|") ||
    hiddenColumns.length > 0 ||
    density !== "standard";

  useEffect(() => {
    hiddenColumns = Object.keys(visibilityModel).filter(
      (field) => (visibilityModel as Record<string, boolean>)[field] === false,
    );
  }, [visibilityModel]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget.parentElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeDensity = (newDensity: string) => {
    apiRef.current.setDensity(newDensity);
  };

  const handleChangeLayout = (newLayout: string) => {
    apiRef.current.restoreState({
      unstable_listView: newLayout == "grid" ? true : false,
    });
  };

  const handleChangeColumnVisibility = (col, e) => {
    apiRef.current.setColumnVisibility(col.field, e.target.checked);
  };

  const reorder = (items: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(items);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleDragEnd = ({
    destination,
    source,
  }: {
    destination: DraggableLocation;
    source: DraggableLocation;
  }) => {
    if (!destination || destination.index === source.index) return;
    // `source.index` / `destination.index` are positions in `orderedColumns` —
    // the SAME array the <Draggable index={index}> props come from — so index it
    // directly (indexing the filtered list would be off by any leading pinned col).
    const movingField = orderedColumns[source.index]?.field;
    const dropField = orderedColumns[destination.index]?.field;
    if (!movingField) return;
    // setColumnIndex wants the target position in the grid's FULL visible column
    // array (which may be offset by a checkbox / pinned-left column). Translate the
    // drop slot via the field currently sitting there.
    const liveFields = apiRef.current.getAllColumns().map((c) => c.field);
    const target = dropField
      ? liveFields.indexOf(dropField)
      : liveFields.length - 1;
    if (target < 0) return;
    apiRef.current.setColumnIndex(movingField, target);
  };

  // navChrome, so this menu is the same object as every other menu. It had its own pair —
  // brand @0.12 hover and @0.16 selected — which were the app-wide values BEFORE they were
  // lowered, so once the shared token moved these were left brighter than every other dropdown.
  // That is exactly the drift a local copy guarantees: the values were right when written.
  //
  // Hover is now NEUTRAL and selected is brand (see navChrome for why the two states differ).
  const BRAND_HOVER = {
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: navHover(theme),
    },
  };

  // Both row styles take their padding + radius from the house menu constants rather
  // than restating them. `p: 3` already resolved to the house 12px, so the only visible
  // change is the RADIUS: it was 2 (8px), the SAME radius as the paper containing it,
  // which defeats the concentric nesting MENU_ITEM_SX exists to give (an inner corner
  // wants a smaller radius than the corner it sits inside).
  const SizingMenuItemSX = {
    ...MENU_ITEM_SX,
    // The gap sits ABOVE these rows, not below. The first follows a `ListSubheader` and
    // the last is followed by a `Divider`, so the house trailing margin would double up
    // against both.
    mb: 0,
    mt: 1,
    justifyContent: "space-between",
    ...BRAND_HOVER,
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: navSelected(theme),
    },
  };

  const OrderMenuItemSX = {
    ...MENU_ITEM_DENSE_SX,
    // Denser than the house dense row (6px). These rows carry a drag grip and a
    // checkbox, so the CONTROL heights set the row height and the extra 2px of padding
    // only adds slack to a list that is already the tallest thing in this menu.
    p: 1,
    paddingLeft: 3,
    mb: 0,
    mt: 1,
    justifyContent: "space-between",
    ...BRAND_HOVER,
  };

  return (
    /* `enterDelay` 600, not 3000 (VRG-535). Three seconds is long enough that the tooltip
       effectively did not exist — nobody rests on a button that long — which was tolerable
       while the label was always visible and the tooltip merely redundant. It is not
       tolerable now: at a narrow container this button is icon-only and the tooltip is the
       ONLY thing carrying the word "Layout". 600 matches Download and the bulk-actions
       toggle, so the whole row answers to one delay. */
    <Tooltip title="Layout" enterDelay={600}>
      <Stack>
        <div style={{ display: "relative" }}>
          <Button
            id="layout-options-button"
            disableElevation
            disabled={disabled}
            onClick={handleClick}
            variant="secondary"
            size="large"
            // The accessible name. The visible "Layout" span is `display: none` at a narrow
            // container, which removes it from the accessibility tree as well as the layout.
            aria-label="Layout"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TableIcon color={theme.palette.primary[800]} strokeWidth={1} />
              {/* Label collapses to icon-only as the TOOLBAR CONTAINER narrows — LAST of the
                  five to go (VRG-535, `toolbarCollapse.ts`), because `TableIcon` is the
                  weakest glyph in the row and "Layout" names a concept the product is still
                  teaching rather than one the reader already holds. It was a VIEWPORT query
                  at `md`, which never fired at the width the row actually wraps at.

                  No `toolbarStartIconSx` counterpart: the icon and label are siblings of
                  this `Stack`, whose `spacing` puts the gap on the LABEL, so hiding the
                  label takes its gap with it. */}
              <Box
                component="span"
                sx={{ ...toolbarLabelSx("layout"), fontSize: 16 }}
              >
                Layout
              </Box>
            </Stack>
          </Button>
          <Menu
            id="layout-options-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            sx={{
              pb: 0,
              pt: 0,
              maxHeight: vhMinus(70),
            }}
            MenuListProps={{
              "aria-labelledby": "layout-options-button",
              sx: {
                minWidth: "280px",
                pb: 0,
                pt: 0,
              },
            }}
            slotProps={{
              paper: {
                sx: (theme) => ({
                  // The house trigger→menu gap (`MENU_PAPER_SX`), stated rather than
                  // spread: that constant's `overflowY: auto` + `maxHeight` would hand
                  // scrolling to the Paper, and this menu's drag-reorder list and its
                  // `ListSubheader` need the Paper to stay unclipped. Absolute px, not
                  // `mt: 1` (which rendered 4px on the platform's 4px base).
                  mt: '8px',
                  p: 2,
                  pt: 0,
                  borderRadius: 2,
                  // Match the other core toolbar dropdowns (Filter drawer + the
                  // @repo/ui Select used by Sort): background.paper, not white[100].
                  // In dark mode white[100] is zinc #27272a (a lighter gray that
                  // stood out); background.paper is #0f0f12 to match. Flat (no dark
                  // elevation overlay / gray glow).
                  backgroundColor: theme.palette.background.paper,
                  backgroundImage: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 3,
                }),
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            keepMounted
          >
            {/* TODO: implement GRID option
                <ListSubheader>Layout</ListSubheader>
                <MenuItem 
                    key="layout-table"
                    onClick={() => handleChangeLayout('table')} 
                    selected={layout == 'table'}
                    sx={MenuItemSX}>
                    <Stack direction="row" spacing={3} alignItems="center">
                        <TableIcon strokeWidth={1}/>
                        <Typography sx={{ml:6}}>Table</Typography>
                    </Stack>
                    {layout == 'table' && <CheckIcon strokeWidth={1}/>}
                </MenuItem>
                <MenuItem 
                    key="layout-grid" 
                    onClick={() => handleChangeLayout('grid')} 
                    selected={layout == 'grid'}
                    sx={MenuItemSX}>
                    <Stack direction="row" spacing={3} alignItems="center">
                        <LayoutGridIcon strokeWidth={1}/>
                        <Typography sx={{ml:6}}>Grid</Typography>
                    </Stack>
                    {layout == 'grid' && <CheckIcon strokeWidth={1}/>}
                </MenuItem>
                <Divider flexItem/>  */}
            <ListSubheader>Row sizing</ListSubheader>
            <MenuItem
              key="density-compact"
              onClick={() => handleChangeDensity("compact")}
              selected={density == "compact"}
              sx={SizingMenuItemSX}
            >
              <Stack direction="row" spacing={3} alignItems="center">
                <Rows4Icon strokeWidth={1} />
                <Typography variant="body2" sx={{ ml: 6 }}>Compact</Typography>
              </Stack>
              {density == "compact" && <CheckIcon strokeWidth={1} />}
            </MenuItem>
            <MenuItem
              key="density-standard"
              onClick={() => handleChangeDensity("standard")}
              selected={density == "standard"}
              sx={SizingMenuItemSX}
            >
              <Stack direction="row" spacing={3} alignItems="center">
                <Rows3Icon strokeWidth={1} />
                <Typography variant="body2" sx={{ ml: 6 }}>Standard</Typography>
              </Stack>
              {density == "standard" && <CheckIcon strokeWidth={1} />}
            </MenuItem>
            <MenuItem
              key="density-comfortable"
              onClick={() => handleChangeDensity("comfortable")}
              selected={density == "comfortable"}
              sx={SizingMenuItemSX}
            >
              <Stack direction="row" spacing={3} alignItems="center">
                <Rows2Icon strokeWidth={1} />
                <Typography variant="body2" sx={{ ml: 6 }}>Comfortable</Typography>
              </Stack>
              {density == "comfortable" && <CheckIcon strokeWidth={1} />}
            </MenuItem>
            <Divider flexItem sx={{ pt: 1 }} />
            <ListSubheader
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pr: 2,
              }}
            >
              <span>Columns</span>
              {onResetColumns && isLayoutModified && (
                <Typography
                  component="button"
                  onClick={() => onResetColumns()}
                  sx={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    p: 0,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1,
                    // Same white as the rest of the menu text + a persistent underline so it reads
                    // as a link (parity with the filters "Clear"). Only rendered when non-default.
                    color: theme.palette.text.primary,
                    textDecoration: "underline",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Reset
                </Typography>
              )}
            </ListSubheader>
            <>
              <MenuItem disabled={true} sx={{ pl: 3 }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <GripVerticalIcon strokeWidth={1} />
                  <Checkbox
                    edge="start"
                    checked={true}
                    inputProps={{ "aria-labelledby": orderedColumns[0].headerName }}
                  />
                  <Typography variant="body2" sx={{ ml: 6 }}>
                    {orderedColumns[0].headerName}
                  </Typography>
                </Stack>
              </MenuItem>
              <Divider flexItem variant="middle" />
            </>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="droppable">
                {(
                  provided: DroppableProvided,
                  snapshot: DroppableStateSnapshot
                ) => (
                  <Stack
                    key={`droppable`}
                    justifyContent="flex-start"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      background: snapshot.isDraggingOver
                        ? theme.palette.action.hover
                        : "transparent",
                    }}
                    sx={{
                      height: "100%",
                      overflowX: "hidden",
                      flexShrink: 0,
                      fontSize: "12px",
                    }}
                  >
                    {orderedColumns.map((col, index) => {
                      return (
                        <React.Fragment key={`layout-col-${col.field}`}>
                          {col.pinned != "right" && col.pinned != "left" && (
                            <Draggable
                              key={col.field}
                              index={index}
                              draggableId={col.field}
                            >
                              {(
                                provided: DraggableProvided,
                                snapshot: DraggableStateSnapshot
                              ) => {
                                return (
                                  <div
                                    key={`draggable-${col.field}`}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      background: snapshot.isDragging
                                        ? theme.palette.grey[100]
                                        : "transparent",
                                    }}
                                  >
                                    <MenuItem
                                      key={`listitem-${col.field}`}
                                      onClick={() => handleChangeLayout(col)}
                                      sx={OrderMenuItemSX}
                                      {...provided.dragHandleProps}
                                    >
                                      <Stack
                                        key={`column-${col.field}`}
                                        direction="row"
                                        spacing={3}
                                        alignItems="center"
                                      >
                                        <GripVerticalIcon strokeWidth={1} />
                                        <Checkbox
                                          edge="start"
                                          sx={{
                                            "&.Mui-checked": {
                                              // mode-aware: white check in dark, near-black in light
                                              color: "text.primary",
                                            },
                                          }}
                                          checked={
                                            !hiddenColumns.includes(col.field)
                                          }
                                          tabIndex={-1}
                                          inputProps={{
                                            "aria-labelledby": col.headerName,
                                          }}
                                          onChange={(e) =>
                                            handleChangeColumnVisibility(col, e)
                                          }
                                        />
                                        <Typography variant="body2" sx={{ ml: 6 }}>
                                          {col.headerName}
                                        </Typography>
                                      </Stack>
                                    </MenuItem>
                                  </div>
                                );
                              }}
                            </Draggable>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          </Menu>
        </div>
      </Stack>
    </Tooltip>
  );
};
